<template>
  <div class="w-100 h-100 overflow-hidden bg-light d-flex flex-row">
    <!-- Main Map Area -->
    <div class="position-relative flex-grow-1">
      <MapToolsPanel @zoomToCurrentSelection="handleZoomToCurrentSelection" />
      <PacificMap ref="pacificMap" />
    </div>

    <!-- Permanent desktop sidebar -->
    <DesktopSidebar class="d-none d-lg-flex">
      <IslandNavigationList
        v-if="!selectedItem"
        :islands="getIslands"
        :areas="selectableAreas"
        sort-by="alphabetical"
        :title="selectionTitle"
        @select="handleIslandSelect"
        @selectArea="handleAreaSelect"
      />
      <SelectionDetails v-else :show-title="true" @back="handleBackToList" />
    </DesktopSidebar>

    <!-- Mobile Bottom Offcanvas (md and below) -->
    <MobileSelectionOffcanvas
      :show="showMobileOffcanvas"
      @close="handleCloseMobileOffcanvas"
      :title="selectionTitle"
    >
      <IslandNavigationList
        v-if="!selectedItem"
        :islands="getIslands"
        :areas="selectableAreas"
        sort-by="alphabetical"
        @select="handleIslandSelect"
        @selectArea="handleAreaSelect"
      />
      <SelectionDetails v-else :show-title="false" @back="handleBackToList" />
    </MobileSelectionOffcanvas>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import MapToolsPanel from "@/components/activity/MapToolsPanel.vue";
import DesktopSidebar from "@/components/activity/DesktopSidebar.vue";
import MobileSelectionOffcanvas from "@/components/activity/MobileSelectionOffcanvas.vue";
import SelectionDetails from "@/components/activity/SelectionDetails.vue";
import IslandNavigationList from "@/components/activity/IslandNavigationList.vue";
import PacificMap from "@/components/activity/PacificMap.vue";
import { CulturalRegionHeaders, MigrationRouteHeaders } from "@/types/index.ts";

const MOBILE_WIDTH = 992; // Bootstrap lg breakpoint

export default {
  name: "ActivityView",
  components: {
    MapToolsPanel,
    DesktopSidebar,
    MobileSelectionOffcanvas,
    SelectionDetails,
    IslandNavigationList,
    PacificMap
  },
  data() {
    return {
      // Mobile offcanvas should be open by default to show instructions
      showMobileOffcanvas: window.innerWidth < MOBILE_WIDTH
    };
  },
  computed: {
    ...mapGetters({
      selectedItem: "selectedItem",
      getIslands: "getIslands",
      getCulturalRegions: "getCulturalRegions",
      getMigrationRoutes: "getMigrationRoutes"
    }),
    selectionTitle() {
      if (!this.selectedItem) return "Explore the Pacific";
      // Prefer western/English name when available
      return (
        this.selectedItem.Name ||
        this.selectedItem.name ||
        this.selectedItem["Region Name"] ||
        ""
      );
    },
    /**
     * Creates a list of selectable areas (polygons) combining cultural regions and migration waves.
     * Each area includes the necessary layer information to activate when selected.
     * @returns {Array} Array of area objects for the navigation list
     */
    selectableAreas() {
      const areas = [];

      // Add cultural regions (layer: "cultural")
      this.getCulturalRegions.forEach((region) => {
        areas.push({
          id: region[CulturalRegionHeaders.ID],
          name: region[CulturalRegionHeaders.NAME],
          layer: "cultural",
          type: "region",
          ...region
        });
      });

      // Add migration wave routes (layer: "migration_wave_1" or "migration_wave_2")
      this.getMigrationRoutes.forEach((route) => {
        const routeId = route[MigrationRouteHeaders.ID];
        // Determine which layer based on route ID (wave_1 or wave_2)
        let layer = "none";
        if (routeId === "wave_1") {
          layer = "migration_wave_1";
        } else if (routeId === "wave_2") {
          layer = "migration_wave_2";
        }

        areas.push({
          id: routeId,
          name: route[MigrationRouteHeaders.NAME],
          layer: layer,
          type: "route",
          ...route
        });
      });

      return areas;
    }
  },
  methods: {
    ...mapActions(["clearSelection", "selectItem", "setActiveLayerExclusive"]),
    handleZoomToCurrentSelection() {
      // Call the zoom method on the PacificMap component
      if (this.$refs.pacificMap) {
        this.$refs.pacificMap.zoomToCurrentSelection();
      }
    },
    handleCloseMobileOffcanvas() {
      // Close the mobile offcanvas when user clicks close button or backdrop
      this.showMobileOffcanvas = false;
      this.clearSelection();
    },
    handleCloseSelection() {
      this.clearSelection();
    },
    /**
     * Handles island selection from the navigation list
     * @param {Object} island - The selected island object (with type property)
     */
    handleIslandSelect(island) {
      this.selectItem(island);
    },
    /**
     * Handles area/polygon selection from the navigation list.
     * First activates the appropriate layer, then selects the item.
     * @param {Object} area - The selected area object with layer info
     */
    handleAreaSelect(area) {
      // Activate the appropriate layer before selecting the area
      // This ensures the polygon is visible on the map when selected
      if (area.layer && area.layer !== "none") {
        this.setActiveLayerExclusive(area.layer);
      }

      // Select the item (same as islands, will show in SelectionDetails)
      this.selectItem(area);
    },
    /**
     * Handles back button click to return to the island list
     */
    handleBackToList() {
      this.clearSelection();
    }
  },
  watch: {
    selectedItem: {
      handler(newVal) {
        if (window.innerWidth < MOBILE_WIDTH) {
          this.showMobileOffcanvas = true;
        }
      }
    }
  }
};
</script>

<style scoped>
/* All styling is now handled by Bootstrap utility classes */
</style>
